// Module-level concurrency limiter (singleton). Caps the number of in-flight
// async tasks so a large compare fan-out — up to N devices × { /readings-fast,
// /battery-readings } — can't stampede the API in one burst. React Query still
// owns caching/retries/state; this only gates the actual fetch.
//
// IMPORTANT: the exported limiter must be a single module-level instance created
// once at import. A per-render/per-hook instance would give every render its own
// pool and concurrency would silently multiply back to unbounded.

type Task<T> = () => Promise<T>

function createLimiter(maxConcurrent: number) {
  let active = 0
  const queue: Array<() => void> = []

  const pump = () => {
    while (active < maxConcurrent && queue.length > 0) {
      const run = queue.shift()!
      active++
      run()
    }
  }

  return function limit<T>(task: Task<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      queue.push(() => {
        task().then(resolve, reject).finally(() => {
          active--
          pump()
        })
      })
      pump()
    })
  }
}

// Shared across ALL compare fan-out fetches (readings + battery), so the cap
// bounds total in-flight requests — not per-endpoint.
export const compareFetchLimit = createLimiter(5)
